import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { Company, User } from "@core/models";
import { BookedService, CompanyService, NgxToastrService, TrainService, TravelService, UserService, UtilsService } from "@core/services";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { ExpiredModalComponent } from "@shared/component/expired-modal/expired-modal.component";
import moment from "moment";
import { firstValueFrom, Subject, Subscription, take, takeUntil } from "rxjs";
import swal from "sweetalert2";

@Component({
  selector: "app-details",
  templateUrl: "./details.component.html",
  styleUrls: ["./details.component.scss"],
})
export class DetailsComponent implements OnInit, OnDestroy {
  @Input() authorization: any;
  @Input() user: any;
  @Input() fromD: string;
  @Input() toD: string;
  @Output() sendInfo = new EventEmitter<any>();
  @Output() authorizationDeleted = new EventEmitter<void>();
  isDeleting = false;
  isCheckingRate = false;
  private checkRateCorrelationId: string;
  private destroyed = false;
  private destroy$ = new Subject<void>();
  @ViewChild("textarea") textarea;
  authorizationUser: User;
  subscriptions: Subscription[] = [];
  travellersWithFFDocuments: { fullName: string, documentNumber: string }[] = [];
  priceFromCheck: number;
  companyFromUser: any;
  authTravel: any;
  formattedTravellers: string = '';
  private expiredModalOpen = false;
  private expiredHandledCorrelationIds = new Set<string>();
  groupedBaggagesInfo: Array<{ route: string; items: string[] }> = [];

  constructor(
    private ngxToastrService: NgxToastrService,
    private translateService: TranslateService,
    private modal: NgbModal,
    private userService: UserService,
    private bookedService: BookedService,
    public utilsService: UtilsService,
    private companyService: CompanyService,
    private travelService: TravelService,
    public trainService: TrainService
  ) {}

  private readonly checkRateListener = (result) => {
        const cid = result?.correlationId;

        try {
          if (this.destroyed || !this.shouldCheckRate || !cid || cid !== this.checkRateCorrelationId) return;

          this.checkRateCorrelationId = undefined;
          this.isCheckingRate = false;

          const data = result?.data;

          const isExpired =
            data?.statusCode === 817 || data?.errorCode === "TOKEN_EXPIRED";

          if (isExpired) {
          const key = `auth:${cid}`;

          if (this.expiredHandledCorrelationIds.has(key)) return;
          this.expiredHandledCorrelationIds.add(key);

          if (this.expiredModalOpen) return;
          this.expiredModalOpen = true;

          try { this.modal.dismissAll(); } catch {}

          const modalRef = this.modal.open(ExpiredModalComponent, {
            windowClass: "custom-modal-expired",
            centered: true,
            backdrop: "static",
            keyboard: false,
          });

          modalRef.componentInstance.redirectTo = `/`;

          modalRef.result.finally(() => {
            this.expiredModalOpen = false;
            this.expiredHandledCorrelationIds.clear();
          });

            return;
          }

          if (
            data?.status === "PriceChanged" &&
            data?.errorCode === "PRICE_CHANGED_WITHOUT_NEW_PRICE"
          ) {
            this.handleHotelPriceChangedWithoutNewPrice(data);
            return;
          }

          if (data?.status === "PriceChanged" || data?.priceChanged) {
            this.handleHotelPriceChanged(data);
            return;
          }

          if (
            data?.type?.toLowerCase?.().includes("error") ||
            data?.status === "SoldOut" ||
            data?.errorCode === "INSUFFICIENT_ALLOTMENT" ||
            data?.errorCode === "STOP_SALES"
          ) {
            this.handleHotelUnavailable(data);
            return;
          }

          let paxes = this.authorization.requestData.rooms[0].paxes;

          this.authorization.requestData.rooms[0] = data.accommodation.rooms[0].rates
            ? data.accommodation.rooms[0]?.rates[0]
            : data.accommodation.rooms[0];

          if (!this.authorization?.requestData?.rooms[0]?.paxes) {
            this.authorization.requestData.rooms[0].paxes = paxes;
          }

          if (data.price?.totalPrice?.total) {
            this.priceFromCheck = data.price.totalPrice.total;
          }
        } catch (error) {
          this.isCheckingRate = false;
        }
  };

  private get shouldCheckRate(): boolean {
    return this.authorization?.type?.toLowerCase() === "booking" &&
      this.authorization?.status?.toLowerCase() === "request";
  }

  ngOnInit(): void { 
    if(this.authorization.travelUuid){
      this.travelService.getTravel(this.authorization.travelUuid).subscribe(res => {
        this.authTravel = res;
      });
    }

    this.priceFromCheck = this.authorization?.price || 0;
    if (this.authorization.type.toLowerCase() === "flight") {
      const travellers = this.authorization?.requestData?.query?.travellers;
      if (travellers && Array.isArray(travellers)) {
        travellers.forEach(traveller => {
          const documents = traveller?.documents;
          if (documents && Array.isArray(documents)) {
            const document = documents.find(doc => doc.type === 'FF');
            if (document && document?.number) {
              const travellerName = traveller.name;
              const travellerSurnames = traveller.surnames;
              const documentNumber = document.number;
              
              this.travellersWithFFDocuments.push({
                fullName: `${travellerName} ${travellerSurnames.join(' ')}`,
                documentNumber: documentNumber
              });
            }
          }
        });
      }
      if (this.authorization?.requestData?.query?.travellers?.length > 0) {
        this.formattedTravellers = this.authorization.requestData.query.travellers
          .map(t => t.name + ' ' + (t.surnames?.join(' ') || ''))
          .join(', ');
      }
      this.groupedBaggagesInfo = this.getGroupedBaggagesInfo();
    }
    if (this.authorization?.requestData?.passengers?.length > 0) {
      this.formattedTravellers = this.authorization.requestData.passengers
        .map(t => t.name + ' ' + (t.surname || '') + ' ' + (t.lastSurname || ''))
        .join(', ');
    }

    if (this.shouldCheckRate) {
      const parsedRoom = {...this.authorization.requestData?.rooms[0]};
      parsedRoom.nameRoom = this.authorization.requestData?.rooms[0].name;
      parsedRoom.net = this.authorization.requestData?.rooms?.[0]?.rates?.[0]?.net || this.authorization.requestData?.rooms?.[0]?.net;
      parsedRoom.rateType = this.authorization.requestData?.rooms?.[0]?.rates?.[0]?.rateType || this.authorization.requestData?.rooms?.[0]?.rateType;
      parsedRoom.rateKey = this.authorization.requestData?.rooms?.[0]?.rates?.[0]?.rateKey || this.authorization.requestData?.rooms?.[0]?.rateKey;
      parsedRoom.cancellation = !!this.authorization.requestData?.rooms?.[0]?.rates?.[0]?.cancellationPolicies
      parsedRoom.cancellationPolicies = this.authorization.requestData?.rooms?.[0]?.rates?.[0].cancellationPolicies
      if(parsedRoom.cancellationPolicies){
        parsedRoom.cancellationPolicies?.forEach((item, index, array) => {
          array[index] = {
            amount: item.amount,
            from: item.from,
            text: item.text,
          }
        });
      }
      const body = {
        userUuid: this.authorization.userUuid,
        checkIn: moment(this.authorization.fromDate).format("YYYY-MM-DD"),
        checkOut: moment(this.authorization.toDate).format("YYYY-MM-DD"),
        rooms: [parsedRoom],
        guests: this.authorization.requestData?.guests,
        isAuthorization: true,
        authorizationUuid: this.authorization.uuid,
        price: this.authorization.price || this.authorization.requestData?.rooms?.[0]?.totalWithPercentage
      };
      const correlation = crypto.randomUUID();
      this.checkRateCorrelationId = correlation;
      this.isCheckingRate = true;
      this.utilsService.socket.on("check-rate-authorization", this.checkRateListener);
      this.utilsService.correlationId.push(correlation);
      body['correlationId'] = correlation;
      this.authorization.requestData.rooms[0].correlationId = correlation;
      this.bookedService.checkRate(body).pipe(takeUntil(this.destroy$)).subscribe({
        error: () => {
          this.checkRateCorrelationId = undefined;
          this.isCheckingRate = false;
        }
      });
    }
    this.subscriptions.push(
      this.userService.getUser(this.authorization.userUuid).subscribe((user: User) => {
        this.authorizationUser = user;
        if(user.companyUuid){
          this.companyService.getCompany(user.companyUuid).subscribe(
            (company: Company) => {
              this.companyFromUser = company;
            },
            (error) => {}
          )
        }
      })
    )
  }

  getHours(authorization, departure: boolean) {
    let departHour: string;
    let returnHour: string;
    if (authorization.type?.toLowerCase() === "train") {
      departHour = moment(authorization.requestData.departDate).format("HH:mm");
      returnHour =
        authorization.requestData.returnDate != null
          ? moment(authorization.requestData.returnDate).format("HH:mm")
          : "";
    } else {
      if (authorization.requestData.departureDate) {
        departHour = moment(authorization.requestData.departureDate).format("HH:mm");
      } else {
        departHour = authorization.requestData.departDate.split(" ")[1];
      }

      if (authorization.requestData.returnDate && authorization.requestData.returnDate.match(/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/)) {
        returnHour = authorization.requestData.returnDate.split(" ")[1];
      } else {
        returnHour = moment(authorization.requestData.returnDate).format("HH:mm");
      }
    }

    return departure ? departHour : returnHour;
  }

  get canDeleteAuthorization(): boolean {
    return this.user?.type?.toUpperCase() === "EMPLOYEE" &&
      this.authorization?.status === "request";
  }

  async onDelete(): Promise<void> {
    if (!this.canDeleteAuthorization || this.isDeleting) return;
    this.isDeleting = true;

    try {
      const result = await swal.fire({
        title: this.translateService.instant("authorization.delete.title"),
        text: this.translateService.instant("authorization.delete.confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.translateService.instant("common.delete"),
        cancelButtonText: this.translateService.instant("common.cancel"),
        focusCancel: true,
      });
      if (!result.isConfirmed || this.destroyed) return;

      await firstValueFrom(this.bookedService.deleteAuthorization(this.authorization.uuid)
        .pipe(takeUntil(this.destroy$)));
      this.ngxToastrService.typeSuccess(
        this.translateService.instant("authorization.delete.title"),
        this.translateService.instant("authorization.delete.success")
      );
      this.isDeleting = false;
      this.authorizationDeleted.emit();
    } catch (error) {
      if (this.destroyed) return;
      const message = error.status === 409 ? "not-pending" :
        error.status === 404 ? "not-found" :
        error.status === 403 ? "forbidden" : "error";
      this.ngxToastrService.typeError(
        this.translateService.instant("common.error"),
        this.translateService.instant(`authorization.delete.${message}`)
      );
    } finally {
      this.isDeleting = false;
    }
  }

  onDeny(authorization) {
    if (this.isDeleting) return;
    const action = this.textarea.nativeElement;

    if (!action.value || action.value === "") {
      this.translateService
        .get(["common.error", "authorization.swal.comment-required"])
        .subscribe((transl) => {
          this.ngxToastrService.typeInfo(
            transl["common.error"],
            transl["authorization.swal.comment-required"]
          );
        });
    } else {
      const info = {
        action: action.value,
        deny: true,
        authorization: authorization,
        loading: true
      };
      this.sendInfo.emit(info);
    }
  }

  onAccept(authorization) {
    if (this.isDeleting) return;
    const action = this.textarea.nativeElement;
    const info = {
      action: action.value,
      deny: false,
      authorization: authorization,
      loading: true,
      authorizationUser: this.authorizationUser,
      companyFromUser: this.companyFromUser,
      authTravel: this.authTravel
    };
    this.sendInfo.emit(info);
  }

  getLogo(value: string) {
    return `assets/img/airlines/small/${value}.png`;
  }

  hasValidScales(authorization: any): boolean {
    return authorization?.requestData?.scales?.some(
      scale => Array.isArray(scale.layovers) && scale.layovers.length > 0
    );
  }

  getBaggagesInfo(): any[] {
    return this.authorization?.requestData?.query?.ancillaries?.filter(
      (item: any) => item.category === 'Baggage' && item.short_name
    ) || [];
  }

  getPriorityInfo(): any[] {
    return this.authorization?.requestData?.query?.ancillaries?.filter(
      (item: any) => item.category === 'PriorityBoard' && item.short_name
    ) || [];
  }

  getGuestsFullNames(): string[] {
    return this.authorization?.requestData?.guests?.map(
      (guest: any) => `${guest.name} ${guest.surname}`
    ) || [];
  }

  getPassengerNameByUuid(uuid: string): string | null {
    const traveller = this.authorization?.requestData?.query?.travellers?.find(t => t.uuid === uuid);
    if (!traveller) return null;

    const surnames = traveller.surnames?.join(' ') || '';
    return `${traveller.name} ${surnames}`.trim();
  }

  getSeatsInfoFromQuery() {
    const seats = this.authorization?.requestData?.query?.seats || [];

    const groupedByPassenger = seats.reduce((acc, seat) => {
      const uuid = seat.uuid;
      if (!acc[uuid]) {
        acc[uuid] = {
          passengerUuid: uuid,
          seats: [],
        };
      }
      acc[uuid].seats.push(seat);
      return acc;
    }, {} as Record<string, { passengerUuid: string; seats: any[] }>);

    return Object.values(groupedByPassenger);
  }

  getGroupedBaggagesInfo(): Array<{ route: string; items: string[] }> {
    const ancillaries = this.authorization?.requestData?.query?.ancillaries;

    if (!Array.isArray(ancillaries) || ancillaries.length === 0) {
      return [];
    }

    const baggages = ancillaries.filter(
      (item: any) => item?.category === "Baggage" && item?.short_name
    );

    if (!baggages.length) {
      return [];
    }

    const grouped = new Map<string, Set<string>>();

    for (const baggage of baggages) {
      const departure = baggage?.departure?.trim();
      const arrival = baggage?.arrival?.trim();

      const route =
        departure && arrival
          ? `${departure} → ${arrival}`
          : baggage?.legType === "outbound"
          ? this.translateService.instant("train.confirm-stepper-container.seat-selection.outward")
          : baggage?.legType === "return"
          ? this.translateService.instant("train.confirm-stepper-container.seat-selection.return")
          : this.translateService.instant("train.form-train.unspecified-route");

      if (!grouped.has(route)) {
        grouped.set(route, new Set<string>());
      }

      grouped.get(route)?.add(baggage.short_name);
    }

    return Array.from(grouped.entries()).map(([route, items]) => ({
      route,
      items: Array.from(items),
    }));
  }

  handleHotelPriceChanged(data: any): void {
    const previousPrice = Number(data?.priceChanged?.previousPrice || this.authorization?.price || 0).toFixed(2);
    const currentPrice = Number(data?.priceChanged?.currentPrice || 0).toFixed(2);
    const currency = data?.priceChanged?.currency || "EUR";

    this.translateService
      .get([
        "flight.detail-flight.price-changed",
        "flight.detail-flight.previous-price",
        "flight.detail-flight.current-price",
        "flight.detail-flight.change-detected",
        "project.accept",
        "common.cancel",
      ])
      .pipe(take(1))
      .subscribe((translations) => {
        swal.fire({
          icon: "warning",
          title: translations["flight.detail-flight.change-detected"],
          html: `
            <p>${translations["flight.detail-flight.price-changed"]}</p>
            <p><strong>${translations["flight.detail-flight.previous-price"]}:</strong> ${previousPrice} ${currency}</p>
            <p><strong>${translations["flight.detail-flight.current-price"]}:</strong> ${currentPrice} ${currency}</p>
          `,
          showCancelButton: true,
          confirmButtonText: translations["project.accept"],
          cancelButtonText: translations["common.cancel"],
          customClass: {
            popup: "price-change-popup",
            confirmButton: "simple-confirm-btn",
            cancelButton: "simple-cancel-btn",
          },
          buttonsStyling: false,
        }).then((result) => {
          if (!result.isConfirmed) {
            this.close();
            return;
          }

          const checkedRoom = data?.checkedRoom;
          const checkedRate = data?.checkedRate || checkedRoom?.rates?.[0];

          if (checkedRate) {
            const previousPaxes = this.authorization?.requestData?.rooms?.[0]?.paxes || [];

            this.authorization.requestData.rooms[0] = {
              ...this.authorization.requestData.rooms[0],
              ...checkedRate,
              paxes: checkedRate.paxes || previousPaxes,
              totalWithPercentage: currentPrice,
            };
          }

          if (checkedRoom) {
            this.authorization.requestData.checkedRoom = checkedRoom;
          }

          this.authorization.price = Number(currentPrice);
          this.priceFromCheck = Number(currentPrice);

          this.authorization.priceChanged = {
            accepted: true,
            previousPrice: Number(previousPrice),
            currentPrice: Number(currentPrice),
            currency,
            checkedRate,
            checkedRoom,
          };
        });
      });
  }

  handleHotelPriceChangedWithoutNewPrice(data: any): void {
    this.translateService
      .get([
        "hotel.priceChanged.updated-title",
        "hotel.priceChanged.without-new-price",
        "common.accept",
      ])
      .pipe(take(1))
      .subscribe((translations) => {
        const text =
          data?.userMessage ||
          translations["hotel.priceChanged.without-new-price"];

        swal.fire({
          icon: "warning",
          title: translations["hotel.priceChanged.updated-title"],
          text,
          showCancelButton: false,
          confirmButtonText: translations["common.accept"],
          customClass: {
            popup: "price-change-popup",
            confirmButton: "simple-confirm-btn",
          },
          buttonsStyling: false,
        }).then(() => {
          this.close();
        });
      });
  }

  handleHotelUnavailable(data: any): void {
    const messageKey = this.getHotelUnavailableMessageKey(data);

    this.translateService
      .get([
        "hotel.errors.unavailable-title",
        messageKey,
        "project.accept",
      ])
      .pipe(take(1))
      .subscribe((translations) => {
        const message =
          data?.userMessage ||
          data?.message ||
          translations[messageKey];

        swal.fire({
          icon: "warning",
          title: translations["hotel.errors.unavailable-title"],
          text: message,
          showConfirmButton: true,
          confirmButtonText: translations["project.accept"],
          customClass: {
            popup: "price-change-popup",
            confirmButton: "simple-confirm-btn",
          },
          buttonsStyling: false,
        }).then(() => {
          this.close();
        });
      });
  }

  private getHotelUnavailableMessageKey(data: any): string {
    if (data?.errorCode === "INSUFFICIENT_ALLOTMENT") {
      return "hotel.errors.insufficient-allotment";
    }

    if (data?.errorCode === "STOP_SALES") {
      return "hotel.errors.stop-sales";
    }

    return "hotel.errors.unavailable";
  }

  close() {
    this.modal.dismissAll();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.checkRateCorrelationId = undefined;
    this.isCheckingRate = false;
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.utilsService.socket.off('check-rate-authorization', this.checkRateListener);
  }

  getCarriers() {
    const totalCarriers = this.authorization.requestData.trainsInfo.selectedTrains.selectedOutwardTrain.sections.map((l) => l.carrier)
    if (this.authorization.requestData.trainsInfo.selectedTrains.selectedReturnTrain) {
      this.authorization.requestData.trainsInfo.selectedTrains.selectedOutwardTrain.sections.forEach((l) => totalCarriers.push(l.carrier))
    }
    return totalCarriers
  }

  get trips() {
    return [
      {
        type: 'outward',
        carriers: this.authorization.requestData.trainsInfo.selectedTrains.selectedOutwardTrain?.sections?.map(l => l.carrier) || [],
        sections: this.authorization.requestData.trainsInfo.selectedTrains.selectedOutwardTrain?.sections || []
      },
      {
        type: 'return',
        carriers: this.authorization.requestData.trainsInfo.selectedTrains.selectedReturnTrain?.sections?.map(l => l.carrier) || [],
        sections: this.authorization.requestData.trainsInfo.selectedTrains.selectedReturnTrain?.sections || []
      }
    ].filter(t => t.carriers.length);
  }
}
